import { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '~/modules/store';
import { TestRunActions } from '~/modules/testRun/testRun.redux';
import { UIStateActions } from '~/modules/uiState/uiState.redux';
import { selectHasNotStartedRecording } from '~/modules/user/user.selectors';
import analytics from '~/services/analytics';
import { isBadRequestError } from '~/services/api/utils';
import { ACTIONS } from '~/services/toasts/internalServerError';
import { useTestRunnerActionsState } from '~/views/TestDetails/components/TestActions/TestHeaderActions.hooks';

import { testsApi } from '../../tests.api';
import { showTestError } from '../../tests.utils';

export const useRecordTest = () => {
  const dispatch = useAppDispatch();
  const [recordTest, recordTestState] = testsApi.useStartRecordingMutation();
  const hasNotStartedRecording = useAppSelector(selectHasNotStartedRecording);
  const { setPendingAction, resetPendingAction } = useTestRunnerActionsState();

  const handleRecordTest = useCallback(
    async (params: Parameters<typeof recordTest>[0]) => {
      setPendingAction('record');

      const result = recordTest({
        ...params,
        startAfterStepId: params.startAfterStepId || null,
      }).unwrap();

      result
        .then((testRun) => {
          dispatch(TestRunActions.setCurrentTestRun(testRun));

          if (hasNotStartedRecording) {
            analytics.trackEvent('first_recording_started');
          }
          dispatch(UIStateActions.resetRecentlyAddedStepsIds());
        })
        .catch((error) => {
          if (isBadRequestError(error)) {
            showTestError(ACTIONS.START_RECORD, undefined, true);
          } else {
            showTestError(ACTIONS.START_RECORD, error, true);
          }

          dispatch(UIStateActions.disablePlaybackSelectedPositions());
        })
        .finally(() => {
          resetPendingAction();
        });

      return result;
    },
    [setPendingAction, recordTest, dispatch, hasNotStartedRecording, resetPendingAction],
  );

  return useMemo(
    () => [handleRecordTest, recordTestState] as const,
    [handleRecordTest, recordTestState],
  );
};
